import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Link } from '@atlassian/react-resource-router';
import { MENU_ITEM_HEIGHT } from '../../constants/index.tsx';

type Props = {
	children: ReactNode;
	elemAfter?: ReactNode;
	to?: string;
	href?: string;
	prefetch?: 'hover';
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onClick?: () => any;
	isActive?: boolean;
	className?: string;
	testId?: string;
};

const SidebarLink = ({
	children,
	elemAfter,
	className,
	testId,
	isActive = false,
	...other
}: Props) => (
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
	<Wrapper isActive={isActive} className={className} data-testid={testId}>
		<StyledLink aria-current={isActive ? 'page' : undefined} {...other}>
			{children}
		</StyledLink>
		{elemAfter && (
			<ElementAfterWrapper data-component-selector="sidebar-link-elem-after">
				{elemAfter}
			</ElementAfterWrapper>
		)}
	</Wrapper>
);

const showElementAfter = {
	width: 'inherit !important',
	opacity: 1,
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ElementAfterWrapper = styled.div({
	flex: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	width: '0 !important',
	opacity: 0,
	marginRight: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	'&:focus-within': showElementAfter,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div<{ isActive: boolean }>({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "4px" and fallback "3px" do not match and can't be replaced automatically.
	borderRadius: token('border.radius', '3px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${MENU_ITEM_HEIGHT}px`,
	transition: 'background 0.1s ease-out',
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundColor: ({ isActive }) => !isActive && token('color.background.neutral'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		'[data-component-selector="sidebar-link-elem-after"]': showElementAfter,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:has(a:focus:focus-visible)': {
		outlineWidth: '2px',
		outlineStyle: 'solid',
		outlineOffset: token('space.025'),
		outlineColor: token('color.border.focused'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundColor: ({ isActive }) => !isActive && token('color.background.neutral'),
	},
	'@supports not selector(:has(a:focus:focus-visible))': {
		'&:focus-within': {
			outlineWidth: '2px',
			outlineStyle: 'solid',
			outlineOffset: token('space.025'),
			outlineColor: token('color.border.focused'),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
			backgroundColor: ({ isActive }) => !isActive && token('color.background.neutral'),
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledLink = styled(Link)({
	flex: 1,
	minWidth: 0,
	paddingTop: token('space.100'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.100'),
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle'),
	'&:hover, &:focus, &:focus-visible, &:active': {
		outline: 'none',
		boxShadow: 'none',
		textDecoration: 'none',

		color: token('color.text'),
	},
});

// not memo'd because it accepts children as prop
export default SidebarLink;
